import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Images, PhoneMissed, SearchX } from "lucide-react";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import Faq from "../components/Faq";
import LeadForm from "../components/LeadForm";
import ScreenshotFrame from "../components/ScreenshotFrame";
import BrowserFrame from "../components/BrowserFrame";
import { LogoMark } from "../components/Logo";
import { CONTACT_EMAIL } from "../config";
import { DEMOS } from "../data/demos";
import { usePageMeta } from "../hooks/usePageMeta";
import { scrollToHash } from "../lib/scroll";

const H1_LINES = ["Look as good online", "as you are in person."];

const PROBLEM_CARDS = [
  {
    icon: Images,
    title: "Your best projects are hidden.",
    body: "Great patios, walls, and full-yard transformations end up buried in a camera roll or a social feed instead of front and center.",
  },
  {
    icon: SearchX,
    title: "Homeowners can't tell what you do.",
    body: "Without clear service pages, people can't quickly see that you handle the exact project they're planning.",
  },
  {
    icon: PhoneMissed,
    title: "Asking for an estimate takes too much effort.",
    body: "If there's no simple way to call or request a quote from a phone, many people simply move on.",
  },
];

const CHECKLIST = [
  "A modern website that looks great on phones",
  "5–7 core pages built around your business",
  "A project gallery that shows off your best work",
  "Clear pages for each of your main services",
  "Your real customer reviews, displayed properly",
  "An estimate-request form that sends inquiries straight to you",
  "Tap-to-call buttons on every page",
  "A link to your Google Business Profile",
  "Basic page titles and descriptions set up correctly",
  "Two rounds of revisions",
  "30 days of support after launch",
];

const STEPS = [
  {
    title: "Quick call.",
    body: "A short conversation about your business, your services, and the kind of projects you want more of.",
  },
  {
    title: "Send your materials.",
    body: "Project photos, your services, and any reviews. If you don't have everything organized, we'll help pull it together.",
  },
  {
    title: "Review your site.",
    body: "You see a working draft and request changes. Two full rounds of revisions are included.",
  },
  {
    title: "Launch and support.",
    body: "Your site goes live, and you get 30 days of support for fixes and small updates.",
  },
];

const FAQ_ITEMS = [
  {
    q: "How much does a website cost?",
    a: "Every project is quoted up front based on the size of your business and what you need. You'll get a clear, fixed price before any work starts, with no surprise charges.",
  },
  {
    q: "How long does it take?",
    a: "Most sites are ready for review within a few weeks of receiving your photos and information. The biggest factor is how quickly we can gather your materials.",
  },
  {
    q: "What do I need to provide?",
    a: "Photos of your work, a list of your services, and any reviews you'd like featured. If your photos are scattered across your phone or social media, we'll help you sort out the best ones.",
  },
  {
    q: "I already have a website. Can you still help?",
    a: "Yes. If your current site is outdated, hard to use on phones, or doesn't show your best work, we can rebuild it.",
  },
  {
    q: "Can I make changes after launch?",
    a: "Yes. You get 30 days of support after launch for fixes and small updates. Ongoing updates, like adding new projects to your gallery, are available after that.",
  },
  {
    q: "Will a new website guarantee more customers?",
    a: "No honest designer can guarantee that. What a strong website does is make a better first impression, show your work clearly, and make it easier for interested homeowners to contact you.",
  },
];

function scrollTo(e, hash) {
  e.preventDefault();
  scrollToHash(hash);
}

function DeviceMockup() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yLaptop = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : -14, reduce ? 0 : 26]);
  const yPhone = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : -40, reduce ? 0 : 70]);
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[560px] pb-14 pl-6 sm:pl-10">
      <motion.div style={{ y: yLaptop }}>
        <BrowserFrame url="greenscape-outdoor-design.com">
          <ScreenshotFrame
            src="/images/demo-greenscape-desktop.jpg"
            label="Sample site preview"
            ratio="16 / 10"
            alt="Sample landscaping website shown in a browser window"
          />
        </BrowserFrame>
      </motion.div>
      <motion.div
        style={{ y: yPhone }}
        className="absolute -bottom-2 -left-2 w-[118px] sm:-left-4 sm:w-[142px]"
      >
        <div className="overflow-hidden rounded-[22px] border-4 border-[#262B30] bg-subtle shadow-2xl shadow-black/60">
          <div className="mx-auto mt-2 h-1 w-9 rounded-full bg-[#3A4046]" />
          <ScreenshotFrame
            src="/images/demo-greenscape-mobile.jpg"
            label="Sample site preview"
            ratio="9 / 17"
            alt="Sample landscaping website shown on a phone"
            className="rounded-none border-0"
          />
        </div>
      </motion.div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, intro, align = "left" }) {
  const center = align === "center";
  return (
    <Reveal className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      <p className="eyebrow flex items-center gap-2.5">
        {!center && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />}
        {eyebrow}
      </p>
      <h2 className="h-display mt-5 text-[clamp(30px,4vw,52px)]">{title}</h2>
      {intro && <p className="measure mt-5 text-[17px] text-dim">{intro}</p>}
    </Reveal>
  );
}

export default function Home() {
  const reduce = useReducedMotion();
  usePageMeta({
    title: "Clearriver Studios | Websites for Landscaping & Hardscaping Companies",
    description:
      "Clearriver Studios builds professional websites for landscaping and hardscaping companies, turning your project photos, services, and reviews into a site that makes it easy for homeowners to request an estimate.",
  });

  return (
    <>
      {/* Hero */}
      <section className="hero-glow relative overflow-hidden pt-[72px]" data-testid="hero">
        <div className="container-cr grid items-center gap-14 pb-20 pt-14 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:pb-28 lg:pt-24">
          <div>
            <motion.p
              initial={reduce ? undefined : { opacity: 0, y: 14 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="eyebrow flex items-center gap-2.5"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
              Websites for landscaping &amp; hardscaping companies
            </motion.p>

            <h1 className="h-display mt-6 text-[clamp(44px,7.2vw,88px)]" data-testid="hero-h1">
              {H1_LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={reduce ? undefined : { y: "112%" }}
                    animate={reduce ? undefined : { y: 0 }}
                    transition={{
                      duration: 0.9,
                      delay: 0.14 + i * 0.13,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={reduce ? undefined : { opacity: 0, y: 14 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="measure mt-7 text-[17px] leading-relaxed text-dim sm:text-lg"
              data-testid="hero-sub"
            >
              Clearriver Studios builds websites for landscaping and hardscaping
              companies. We turn your project photos, services, and reviews into a
              professional site that makes it easy for homeowners to request an
              estimate.
            </motion.p>

            <motion.div
              initial={reduce ? undefined : { opacity: 0, y: 14 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.54, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-col gap-3.5 sm:flex-row sm:items-center"
            >
              <a
                href="#contact"
                onClick={(e) => scrollTo(e, "#contact")}
                className="btn-primary"
                data-testid="hero-cta-quote"
              >
                Request a quote
              </a>
              <Link to="/demos" className="btn-secondary" data-testid="hero-cta-demos">
                See sample sites
              </Link>
            </motion.div>

            <motion.p
              initial={reduce ? undefined : { opacity: 0 }}
              animate={reduce ? undefined : { opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.68 }}
              className="mt-8 text-sm text-dim"
            >
              One focused website package &middot; Priced by quote
            </motion.p>
          </div>

          <motion.div
            initial={reduce ? undefined : { opacity: 0, y: 24 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <DeviceMockup />
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* The problem */}
      <section className="py-20 lg:py-32" data-testid="problem-section">
        <div className="container-cr">
          <SectionHeading
            eyebrow="The problem"
            title="Your work is better than your website."
            intro="Most homeowners look a company up before they ever pick up the phone. If what they find doesn't match the quality of your work, they keep scrolling."
          />
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {PROBLEM_CARDS.map((card, i) => (
              <Reveal key={card.title} delay={i * 0.08} className="h-full">
                <div className="card-cr group h-full p-8 transition-transform duration-300 hover:-translate-y-1">
                  <card.icon className="h-6 w-6 text-accent" strokeWidth={1.6} />
                  <h3 className="mt-6 text-[19px] font-semibold leading-snug text-cream">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-[16px] leading-relaxed text-dim">{card.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="border-t border-line bg-elev/40 py-20 lg:py-32" data-testid="offer-section">
        <div className="container-cr">
          <SectionHeading
            eyebrow="What you get"
            title="One focused website, built for how your customers actually decide."
            intro="No bloated packages or endless add-ons. One complete, professional website designed around showing off your work and making it easy to get in touch."
          />
          <Reveal className="mt-14">
            <ul className="grid gap-x-10 gap-y-4 sm:grid-cols-2">
              {CHECKLIST.map((item) => (
                <li key={item} className="flex items-start gap-3.5 py-2.5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10">
                    <Check className="h-3.5 w-3.5 text-accent" strokeWidth={2.5} />
                  </span>
                  <span className="text-[16.5px] leading-relaxed text-cream/90">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              className="mt-10 flex flex-col gap-4 rounded-2xl border border-accent/25 bg-accent/[0.06] p-7 sm:flex-row sm:items-center sm:justify-between"
              data-testid="lead-dashboard-card"
            >
              <div className="max-w-xl">
                <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-accent">
                  Optional: lead dashboard
                </p>
                <p className="mt-2 text-[16px] text-cream/90">
                  Every estimate request is saved to a private dashboard, so no inquiry
                  gets lost in your inbox.
                </p>
              </div>
            </div>
            <a
              href="#contact"
              onClick={(e) => scrollTo(e, "#contact")}
              className="btn-primary mt-10"
              data-testid="offer-cta"
            >
              Request a quote
            </a>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="scroll-mt-20 py-20 lg:py-32" data-testid="process-section">
        <div className="container-cr">
          <SectionHeading
            eyebrow="How it works"
            title="Simple for you. Four steps from first call to launch."
          />
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.09}>
                <div className="border-t border-line pt-8">
                  <span className="font-serif text-6xl leading-none text-dim/70" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-6 text-[19px] font-semibold text-cream">{step.title}</h3>
                  <p className="mt-3 text-[16px] leading-relaxed text-dim">{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sample work preview */}
      <section className="border-t border-line bg-elev/40 py-20 lg:py-32" data-testid="sample-preview-section">
        <div className="container-cr">
          <SectionHeading
            eyebrow="Sample work"
            title="See what a finished site looks like."
            intro="These are concept sites built for a fictional landscaping company to show the style, structure, and features a finished site includes."
          />
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {DEMOS.map((demo, i) => (
              <Reveal key={demo.id} delay={i * 0.1} className="h-full">
                <div className="card-cr flex h-full flex-col p-6 transition-transform duration-300 hover:-translate-y-1 sm:p-8">
                  <BrowserFrame url="sample site">
                    <ScreenshotFrame
                      src={`/images/${demo.desktopShot}`}
                      label={`${demo.title} preview`}
                      ratio="16 / 10"
                      alt={demo.desktopAlt}
                    />
                  </BrowserFrame>
                  <div className="mt-7 flex items-center gap-3">
                    <h3 className="text-[19px] font-semibold text-cream">{demo.title}</h3>
                    <span className="rounded-full border border-line px-3 py-1 text-[11px] font-medium uppercase tracking-[0.1em] text-dim">
                      {demo.tag}
                    </span>
                  </div>
                  <p className="measure mt-3 flex-1 text-[16px] leading-relaxed text-dim">
                    {demo.description}
                  </p>
                  <a
                    href={demo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-accent transition-colors hover:text-accent-hover"
                    data-testid={`home-demo-link-${demo.id}`}
                  >
                    View live demo
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link
              to="/demos"
              className="inline-flex items-center gap-2 text-[15px] font-medium text-cream transition-colors hover:text-accent"
              data-testid="see-all-demos"
            >
              See all sample work
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* About */}
      <section id="about" className="scroll-mt-20 py-20 lg:py-32" data-testid="about-section">
        <div className="container-cr grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow flex items-center gap-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
              About
            </p>
            <h2 className="h-display mt-5 text-[clamp(30px,4vw,52px)]">
              You work directly with the person building your site.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote className="border-l-2 border-accent/60 pl-7 font-serif text-[clamp(20px,2.2vw,26px)] leading-relaxed text-cream/95">
              &ldquo;My job is to fix the gap, so the first impression a homeowner gets
              online matches the quality they&rsquo;d see in person.&rdquo;
            </blockquote>
            <div className="mt-8 space-y-5 text-[16.5px] leading-relaxed text-dim">
              <p>
                I&rsquo;m Andres Guedez, the founder of Clearriver Studios. I started
                this studio to do one thing well: build websites for landscaping and
                hardscaping companies.
              </p>
              <p>
                The companies I work with do impressive, hands-on work, but their online
                presence often doesn&rsquo;t show it. When you work with Clearriver
                Studios, you deal with me directly. No account managers, no handoffs, no
                getting passed around.
              </p>
            </div>
            <p className="mt-9 text-[16px] text-dim">
              <span className="font-serif text-2xl text-cream">Andres Guedez</span>
              <span className="ml-3">Founder</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-20 border-t border-line py-20 lg:py-32" data-testid="faq-section">
        <div className="container-cr">
          <SectionHeading eyebrow="FAQ" title="Common questions." />
          <Reveal className="mt-14 max-w-3xl">
            <Faq items={FAQ_ITEMS} />
          </Reveal>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="scroll-mt-20 py-20 lg:py-32" data-testid="contact-section">
        <div className="container-cr grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <Reveal>
            <p className="eyebrow flex items-center gap-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
              Get in touch
            </p>
            <h2 className="h-display mt-5 text-[clamp(30px,4vw,52px)]">Request a quote.</h2>
            <p className="measure mt-5 text-[17px] text-dim">
              Tell me a little about your business and I&rsquo;ll get back to you
              personally, usually within one business day.
            </p>
            <p className="mt-8 text-[16px] text-dim">
              Prefer email? Reach me directly at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-accent underline-offset-4 hover:underline"
                data-testid="contact-email-link"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <LeadForm />
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="hero-glow border-t border-line py-24 lg:py-36" data-testid="final-cta">
        <div className="container-cr text-center">
          <Reveal>
            <LogoMark size={36} className="mx-auto opacity-80" />
            <h2 className="h-display mx-auto mt-8 max-w-3xl text-[clamp(32px,5vw,60px)]">
              Your work speaks for itself. Your website should too.
            </h2>
            <a
              href="#contact"
              onClick={(e) => scrollTo(e, "#contact")}
              className="btn-primary mt-10"
              data-testid="final-cta-button"
            >
              Request a quote
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
